/**
 * Creates LaTeX code to embed a base64 image directly in the document
 * @param {string} base64Data - The base64 encoded image data
 * @param {string} caption - Optional caption for the image
 * @param {string} width - Width of the image in LaTeX (e.g., "0.8\\textwidth")
 * @param {string} context - Optional context about the image for better labeling
 * @returns {string} - LaTeX code for including the base64 image
 */
export function createBase64ImageLatex(base64Data, caption = "", width = "0.8\\textwidth", context = "") {
    if (!base64Data) {
      console.error("No base64 data provided")
      return "% Error: No image data available"
    }
  
    try {
      // Extract just the base64 part without the data URL prefix
      const base64Only = base64Data.includes(",") ? base64Data.split(",")[1] : base64Data
  
      // Create a unique identifier for this image
      const timestamp = Date.now()
      const randomPart = Math.floor(Math.random() * 1000)
      const imageId = `img_${timestamp}_${randomPart}`
  
      return `% Base64 encoded image
  \\begin{filecontents*}{${imageId}.64}
  ${base64Only}\\end{filecontents*}
  
  \\immediate\\write18{base64 -d ${imageId}.64 > ${imageId}.png}
  
  \\begin{figure}[htbp]
    \\centering
    \\includegraphics[width=${width}]{${imageId}.png}
    ${caption ? `\\caption{${caption}}` : ""}
    \\label{fig:${imageId}}
  \\end{figure}
  `
    } catch (error) {
      console.error("Error creating LaTeX code for base64 image:", error)
      return "% Error creating image LaTeX code"
    }
  }
  
  /**
   * Extracts Python code blocks from a string
   * @param {string} text - Text containing Python code blocks
   * @returns {Array<{code: string, fullMatch: string, context: string}>} - Array of Python code blocks with their full matches and context
   */
  export function extractPythonCodeBlocks(text) {
    if (!text || typeof text !== "string") {
      console.error("Invalid text provided to extract Python code blocks")
      return []
    }
  
    try {
      // This regex matches \`\`\`python followed by any content (including newlines) until the next \`\`\`
      const pythonCodeRegex = /```python\s+([\s\S]*?)```/g
      const matches = []
      let match
  
      while ((match = pythonCodeRegex.exec(text)) !== null) {
        if (match[1] && match[1].trim()) {
          // Try to extract context from surrounding text (e.g., section title or paragraph)
          let context = ""
          const beforeMatch = text.substring(0, match.index).trim()
          const lastParagraphBreak = beforeMatch.lastIndexOf("\n\n")
          if (lastParagraphBreak !== -1) {
            const paragraph = beforeMatch.substring(lastParagraphBreak).trim()
            // Look for section titles or descriptive text
            const sectionMatch =
              paragraph.match(/\\section\{([^}]+)\}/) ||
              paragraph.match(/\\subsection\{([^}]+)\}/) ||
              paragraph.match(/Figure showing (.*?)\.?$/i) ||
              paragraph.match(/Plot (of|for|showing) (.*?)\.?$/i)
  
            if (sectionMatch) {
              context = sectionMatch[1] || sectionMatch[2] || ""
            } else if (paragraph.length < 100) {
              // Use short paragraph as context
              context = paragraph
            }
          }
  
          // Extract plot type from the code
          let plotType = ""
          if (match[1].includes("plt.plot(")) {
            plotType = "line"
          } else if (match[1].includes("plt.bar(")) {
            plotType = "bar"
          } else if (match[1].includes("plt.scatter(")) {
            plotType = "scatter"
          } else if (match[1].includes("plt.hist(")) {
            plotType = "histogram"
          } else if (match[1].includes("plt.pie(")) {
            plotType = "pie"
          }
  
          // Extract title from the code if available
          let title = ""
          const titleMatch = match[1].match(/plt\.title$$['"]([^'"]+)['"]$$/)
          if (titleMatch) {
            title = titleMatch[1]
          }
  
          matches.push({
            code: match[1].trim(),
            fullMatch: match[0], // The entire matched string including \`\`\`python and \`\`\`
            context: context,
            plotType: plotType,
            title: title,
            index: match.index,
          })
        }
      }
  
      return matches
    } catch (error) {
      console.error("Error extracting Python code blocks:", error)
      return []
    }
  }
  
  /**
   * Finds all image inclusions in LaTeX that reference output.png
   * @param {string} latexCode - The LaTeX code to search
   * @returns {Array<{figureEnv: string, caption: string, label: string, type: string}>} - Array of figure environments
   */
  export function findOutputPngFigures(latexCode) {
    if (!latexCode || typeof latexCode !== "string") {
      return []
    }
  
    try {
      const figures = []
  
      // 1. Find standard figure environments that include output.png
      const figureRegex = /\\begin{figure}[\s\S]*?\\includegraphics[^{]*{output\.png}[\s\S]*?\\end{figure}/g
      let match
  
      while ((match = figureRegex.exec(latexCode)) !== null) {
        const figureEnv = match[0]
  
        // Extract caption if available
        let caption = ""
        const captionMatch = figureEnv.match(/\\caption{([^}]*)}/)
        if (captionMatch) {
          caption = captionMatch[1]
        }
  
        // Extract label if available
        let label = ""
        const labelMatch = figureEnv.match(/\\label{([^}]*)}/)
        if (labelMatch) {
          label = labelMatch[1]
        }
  
        figures.push({
          figureEnv,
          caption,
          label,
          index: match.index,
          length: figureEnv.length,
          type: "figure",
        })
      }
  
      // 2. Find center environments with \includegraphics{output.png} and \captionof{figure}
      const centerRegex =
        /\\begin{center}[\s\S]*?\\includegraphics[^{]*{plot\.png}[\s\S]*?\\captionof{figure}{[^}]*}[\s\S]*?\\end{center}/g
  
      while ((match = centerRegex.exec(latexCode)) !== null) {
        const centerEnv = match[0]
  
        // Extract caption from \captionof{figure}
        let caption = ""
        const captionMatch = centerEnv.match(/\\captionof{figure}{([^}]*)}/)
        if (captionMatch) {
          caption = captionMatch[1]
        }
  
        // Extract label if available
        let label = ""
        const labelMatch = centerEnv.match(/\\label{([^}]*)}/)
        if (labelMatch) {
          label = labelMatch[1]
        }
  
        figures.push({
          figureEnv: centerEnv,
          caption,
          label,
          index: match.index,
          length: centerEnv.length,
          type: "center",
        })
      }
  
      // 3. Find any other \includegraphics{output.png} that might not be in a figure or center environment
      const standaloneRegex = /\\includegraphics[^{]*{plot\.png}/g
  
      while ((match = standaloneRegex.exec(latexCode)) !== null) {
        // Check if this match is already part of a figure or center environment we found
        const isPartOfExistingFigure = figures.some((fig) => {
          const figStart = fig.index
          const figEnd = fig.index + fig.length
          return match.index >= figStart && match.index < figEnd
        })
  
        if (!isPartOfExistingFigure) {
          // This is a standalone \includegraphics
          figures.push({
            figureEnv: match[0],
            caption: "",
            label: "",
            index: match.index,
            length: match[0].length,
            type: "standalone",
          })
        }
      }
  
      // Sort figures by their position in the document
      figures.sort((a, b) => a.index - b.index)
  
      return figures
    } catch (error) {
      console.error("Error finding output.png figures:", error)
      return []
    }
  }
  
  /**
   * Replaces Python code blocks with base64 images in LaTeX
   * @param {string} latexCode - The LaTeX code with Python code blocks
   * @returns {Promise<string>} - LaTeX code with Python code blocks replaced by images
   */
  export async function replacePythonCodeWithImages(latexCode) {
    if (!latexCode || typeof latexCode !== "string") {
      console.error("Invalid LaTeX code provided")
      return latexCode || ""
    }
  
    try {
      // Check if this is a Beamer presentation
      const isBeamer =
        latexCode.includes("\\documentclass{beamer}") ||
        (latexCode.includes("\\documentclass[") && latexCode.includes("]{beamer}"))
  

  
      // Step 1: Extract all Python code blocks
      const pythonBlocks = extractPythonCodeBlocks(latexCode)
  
      if (pythonBlocks.length === 0) {
        console.log("No Python code blocks found in LaTeX")
        return latexCode
      }
  
  
      // Step 2: Find all image inclusions in the LaTeX document
      const outputFigures = findOutputPngFigures(latexCode)
  
      // Create a copy of the LaTeX code to work with
      let workingLatexCode = latexCode
  
      // For Beamer, we'll collect all filecontents blocks to move to the preamble
      const filecontentsBlocks = []
      const imageIds = []
  
      // Process image inclusions if they exist
      if (outputFigures.length > 0) {
        // We need to process figures in reverse order to avoid index shifting
        const sortedFigures = [...outputFigures].sort((a, b) => a.index - b.index)
  
        // Generate images for each Python block
        const generatedImages = []
        for (let i = 0; i < Math.min(pythonBlocks.length, outputFigures.length); i++) {
          const pythonBlock = pythonBlocks[i]
  
          try {
            // Generate image from this specific Python block
            const result = await generateImageFromPython(pythonBlock.code)
  
            // Verify we got valid base64 data
            if (!result.base64 || !result.base64.includes("base64")) {
              throw new Error("Invalid base64 image data returned")
            }
  
            // Create a meaningful caption based on context, title, and plot type
            let caption = ""
            if (pythonBlock.title) {
              caption = pythonBlock.title
            } else if (pythonBlock.context) {
              caption = `${pythonBlock.plotType ? `${pythonBlock.plotType.charAt(0).toUpperCase() + pythonBlock.plotType.slice(1)} chart` : "Plot"} for ${pythonBlock.context}`
            } else {
              caption = `Figure ${i + 1}: ${pythonBlock.plotType ? `${pythonBlock.plotType.charAt(0).toUpperCase() + pythonBlock.plotType.slice(1)} chart` : "Plot"}`
            }
  
            generatedImages.push({
              base64: result.base64,
              caption,
              pythonBlock,
              index: i,
            })
          } catch (error) {
            console.error(`Error generating image for Python block ${i + 1}:`, error)
            generatedImages.push({
              error: error.message,
              pythonBlock,
              index: i,
            })
          }
        }
  
        // Replace figures one by one in reverse order
        for (let i = sortedFigures.length - 1; i >= 0; i--) {
            const figure = sortedFigures[i]
          const imageIndex = Math.min(i, generatedImages.length - 1)
          const generatedImage = generatedImages[imageIndex]
  
          if (!generatedImage) {
            console.warn(`No generated image available for figure at index ${imageIndex}`)
            continue
          }
  
          if (generatedImage.error) {
            // If there was an error generating the image, add an error comment
            const errorComment = `
  % Error generating plot from Python code:
  % ${generatedImage.error}
  % Original figure environment preserved
  ${figure.figureEnv}
  `
            workingLatexCode =
              workingLatexCode.substring(0, figure.index) +
              errorComment +
              workingLatexCode.substring(figure.index + figure.length)
            continue
          }
  
          // Create a unique identifier for this image
          const timestamp = Date.now() + i * 1000 // Ensure uniqueness by adding index * 1000
          const randomPart = Math.floor(Math.random() * 1000)
          const imageId = `img_${timestamp}_${randomPart}_${i}`
          imageIds.push(imageId)
  
          // Extract just the base64 part without the data URL prefix
          const base64Only = generatedImage.base64.includes(",")
            ? generatedImage.base64.split(",")[1]
            : generatedImage.base64
  
          // Log the first few characters of the base64 data for debugging
  
          // For Beamer, collect the filecontents blocks
          if (isBeamer) {
            const filecontentsBlock = `
  % Base64 encoded image for figure ${i + 1}
  \\begin{filecontents*}{${imageId}.64}`+`\n`+
  base64Only.trim()+`\\end{filecontents*}
  
  \\immediate\\write18{base64 -d ${imageId}.64 > ${imageId}.png}
  `
            filecontentsBlocks.push(filecontentsBlock)
          }
  
          // Create LaTeX code based on the type of environment
          let replacementCode
  
          if (isBeamer) {
            // For Beamer, just include the image without the filecontents part
            if (figure.type === "center") {
              replacementCode = `
  \\begin{center}
   \\includegraphics[width=0.7\\textwidth]{${imageId}.png}
   \\captionof{figure}{${figure.caption || generatedImage.caption}}
   ${figure.label ? `\\label{${figure.label}}` : `\\label{fig:${imageId}}`}
  \\end{center}
  `
            } else if (figure.type === "standalone") {
              replacementCode = `\\includegraphics[width=0.7\\textwidth]{${imageId}.png}
  `
            } else {
              replacementCode = `
  \\begin{figure}
   \\centering
   \\includegraphics[width=0.7\\textwidth]{${imageId}.png}
   ${figure.caption ? `\\caption{${figure.caption}}` : `\\caption{${generatedImage.caption}}`}
   ${figure.label ? `\\label{${figure.label}}` : `\\label{fig:${imageId}}`}
  \\end{figure}
  `
            }
          } else {
            // For regular LaTeX, include the filecontents part inline
            if (figure.type === "center") {
              replacementCode = `% Base64 encoded image for figure ${i + 1}
  \\begin{filecontents*}{${imageId}.64}`+`\n`+
  base64Only.trim()+`\\end{filecontents*}
  
  \\immediate\\write18{base64 -d ${imageId}.64 > ${imageId}.png}
  
  \\begin{center}
   \\includegraphics[width=0.7\\textwidth]{${imageId}.png}
   \\captionof{figure}{${figure.caption || generatedImage.caption}}
   ${figure.label ? `\\label{${figure.label}}` : `\\label{fig:${imageId}}`}
  \\end{center}
  `
            } else if (figure.type === "standalone") {
              replacementCode = `% Base64 encoded image for figure ${i + 1}
  \\begin{filecontents*}{${imageId}.64}`+`\n`+
  base64Only.trim()+`\\end{filecontents*}
  
  \\immediate\\write18{base64 -d ${imageId}.64 > ${imageId}.png}
  
  \\includegraphics[width=0.7\\textwidth]{${imageId}.png}
  `
            } else {
              replacementCode = `% Base64 encoded image for figure ${i + 1}
  \\begin{filecontents*}{${imageId}.64}`+`\n`+
  base64Only.trim()+`\\end{filecontents*}
  
  \\immediate\\write18{base64 -d ${imageId}.64 > ${imageId}.png}
  
  \\begin{figure}[htbp]
   \\centering
   \\includegraphics[width=0.7\\textwidth]{${imageId}.png}
   ${figure.caption ? `\\caption{${figure.caption}}` : `\\caption{${generatedImage.caption}}`}
   ${figure.label ? `\\label{${figure.label}}` : `\\label{fig:${imageId}}`}
  \\end{figure}
  `
            }
          }
  
          // Replace the figure environment
          workingLatexCode =
            workingLatexCode.substring(0, figure.index) +
            replacementCode +
            workingLatexCode.substring(figure.index + figure.length)
        }
      } else {
        // No image inclusions found, insert images at Python code block locations
        // Sort blocks by their position in the document (ascending order)
        const sortedBlocks = [...pythonBlocks].sort((a, b) => a.index - b.index)
  
        for (let i = 0; i < sortedBlocks.length; i++) {
          const block = sortedBlocks[i]
          const originalIndex = pythonBlocks.findIndex((b) => b === block)
  
          try {
            // Generate image from this specific Python block
            const result = await generateImageFromPython(block.code)
  
            // Verify we got valid base64 data
            if (!result.base64 || !result.base64.includes("base64")) {
              throw new Error("Invalid base64 image data returned")
            }
  
            // Create a meaningful caption
            let caption = ""
            if (block.title) {
              caption = block.title
            } else if (block.context) {
              caption = `${block.plotType ? `${block.plotType.charAt(0).toUpperCase() + block.plotType.slice(1)} chart` : "Plot"} for ${block.context}`
            } else {
              caption = `Figure ${originalIndex + 1}: ${block.plotType ? `${block.plotType.charAt(0).toUpperCase() + block.plotType.slice(1)} chart` : "Plot"}`
            }
  
            // Create a unique identifier for this image
            const timestamp = Date.now() + originalIndex * 1000 // Ensure uniqueness
            const randomPart = Math.floor(Math.random() * 1000)
            const imageId = `img_${timestamp}_${randomPart}_${originalIndex}`
            imageIds.push(imageId)
  
            // Extract just the base64 part without the data URL prefix
            const base64Only = result.base64.includes(",") ? result.base64.split(",")[1] : result.base64
  
            // Log the first few characters of the base64 data for debugging
            console.log(
              `Using base64 data for block ${originalIndex + 1} (first 50 chars): ${base64Only.substring(0, 50)}...`,
            )
  
            // For Beamer, collect the filecontents blocks
            if (isBeamer) {
              const filecontentsBlock = `
  % Base64 encoded image for Python block ${originalIndex + 1}
  \\begin{filecontents*}{${imageId}.64}`+`\n`+
  base64Only.trim()+`\\end{filecontents*}
  
  \\immediate\\write18{base64 -d ${imageId}.64 > ${imageId}.png}
  `
              filecontentsBlocks.push(filecontentsBlock)
            }
  
            // Create LaTeX code for the image
            let imageLatexCode
  
            if (isBeamer) {
              // For Beamer, just include the image without the filecontents part
              imageLatexCode = `
  \\begin{figure}
   \\centering
   \\includegraphics[width=0.7\\textwidth]{${imageId}.png}
   \\caption{${caption}}
   \\label{fig:${imageId}}
  \\end{figure}
  `
            } else {
              // For regular LaTeX, include the filecontents part inline
              imageLatexCode = `% Base64 encoded image for Python block ${originalIndex + 1}
  \\begin{filecontents*}{${imageId}.64}`+`\n`+
  base64Only.trim()+`\\end{filecontents*}
  
  \\immediate\\write18{base64 -d ${imageId}.64 > ${imageId}.png}
  
  \\begin{figure}[htbp]
   \\centering
   \\includegraphics[width=0.7\\textwidth]{${imageId}.png}
   \\caption{${caption}}
   \\label{fig:${imageId}}
  \\end{figure}
  `
            }
  
            // Replace the Python code block with the image
            workingLatexCode = workingLatexCode.replace(block.fullMatch, imageLatexCode)
          } catch (error) {
            console.error(`Error generating image for Python block ${originalIndex + 1}:`, error)
  
            // If there was an error generating the image, add an error comment
            const errorComment = `
  % Error generating plot from Python code:
  % ${error.message}
  % Original Python code preserved:
  % \`\`\`python
  % ${block.code.split("\n").join("\n% ")}
  % \`\`\`
  `
            workingLatexCode = workingLatexCode.replace(block.fullMatch, errorComment)
          }
        }
      }
  
      // Remove any remaining Python code blocks
      const remainingBlocks = extractPythonCodeBlocks(workingLatexCode)
      if (remainingBlocks.length > 0) {
  
        // Sort by position in reverse order to avoid index shifting
        const sortedRemainingBlocks = [...remainingBlocks].sort((a, b) => b.index - a.index)
  
        for (const block of sortedRemainingBlocks) {
          workingLatexCode = workingLatexCode.replace(block.fullMatch, "")
        }
      }
  
      // For Beamer, insert all filecontents blocks in the preamble
      if (isBeamer && filecontentsBlocks.length > 0) {
        // Find the position after \usepackage declarations
        let insertPosition = workingLatexCode.indexOf("\\begin{document}")
        if (insertPosition === -1) {
          // If no \begin{document}, try to find after the last \usepackage
          const lastUsePackage = workingLatexCode.lastIndexOf("\\usepackage")
          if (lastUsePackage !== -1) {
            // Find the end of the line containing the last \usepackage
            const endOfLine = workingLatexCode.indexOf("\n", lastUsePackage)
            if (endOfLine !== -1) {
              insertPosition = endOfLine + 1
            } else {
              // If no newline after \usepackage, append to the end of the preamble
              insertPosition = workingLatexCode.length
            }
          } else {
            // If no \usepackage, insert at the beginning
            insertPosition = 0
          }
        }
  
        // Insert all filecontents blocks at the determined position
        const allFilecontentsBlocks = filecontentsBlocks.join("\n")
        workingLatexCode =
          workingLatexCode.substring(0, insertPosition) +
          "\n% Base64 encoded images\n" +
          allFilecontentsBlocks +
          "\n" +
          workingLatexCode.substring(insertPosition)
      }
  
      return workingLatexCode
    } catch (error) {
      console.error("Error replacing Python code with images:", error)
      return latexCode
    }
  }
  
  /**
   * Generates an image using Python code and returns it as base64
   * @param {string} pythonCode - The Python code to generate the image
   * @returns {Promise<object>} - Object containing base64 image data and Python code
   */
  export async function generateImageFromPython(pythonCode) {
    try {
      // Check if pythonCode is defined and not empty
      if (!pythonCode || typeof pythonCode !== "string") {
        throw new Error("Invalid Python code provided")
      }
  
      const cleanCode = pythonCode.trim()
  
      // Ensure the Python code includes necessary imports and saves the plot
      let modifiedCode = cleanCode
  
      // Add imports if they're not already there
      if (!modifiedCode.includes("import matplotlib.pyplot as plt")) {
        modifiedCode = "import matplotlib.pyplot as plt\nimport numpy as np\n\n" + modifiedCode
      }
  
      // Make sure the plot is saved to a file
      if (!modifiedCode.includes("plt.savefig")) {
        modifiedCode += '\n\nplt.savefig("plot.png")'
      }
  
      // Add a line to close the plot to avoid memory issues
      if (!modifiedCode.includes("plt.close")) {
        modifiedCode += "\nplt.close()"
      }
  
      // Use the Replit endpoint
      const response = await fetch("/api/python-image-generator", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: modifiedCode }),
      })
  
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to generate image from Python code: ${errorText}`)
      }
  
      const data = await response.json()
  
      if (!data.plotBase64) {
        throw new Error("No image data returned from the Python generator")
      }
  
      // Check if the response contains an error
      if (data.error) {
        throw new Error(data.error)
      }
  
      return {
        base64: data.plotBase64,
        pythonCode: modifiedCode,
      }
    } catch (error) {
      console.error("Error generating image from Python:", error)
      throw new Error(`Failed to generate image from Python code: ${error.message}`)
    }
  }
  
  /**
   * Generates a simple placeholder image as base64 when Python generation fails
   * @param {string} message - Error message to display in the image
   * @returns {string} - Base64 encoded SVG image
   */
  export function generatePlaceholderImage(message = "Plot generation failed") {
    const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
    <rect width="400" height="300" fill="#f8f9fa" />
    <text x="200" y="150" font-family="Arial" font-size="16" text-anchor="middle" fill="#dc3545">${message}</text>
    <text x="200" y="180" font-family="Arial" font-size="12" text-anchor="middle" fill="#6c757d">Please check the Python code and try again</text>
  </svg>
    `
  
    // Convert SVG to base64
    const base64 = Buffer.from(svg).toString("base64")
    return `data:image/svg+xml;base64,${base64}`
  }
  
  